import { api } from '@mbscan/client-api'
import type { MeFieldsFragment } from '@mbscan/client-api'
import { isScanAvailable, isSome, metalTagUnavailablePlanName } from '@mbscan/shared'
import { createFileRoute, getRouteApi, Navigate, Outlet, useLocation } from '@tanstack/react-router'
import { useEffect } from 'react'

import { BusinessTransferNotice } from '@/components/business-transfer-notice'
import { CenteredScreen } from '@/components/layout/centered-screen'
import { ErrorRecoveryScreen, TenantAccessDeniedScreen } from '@/components/system-screens'
import type { TenantAccessDeniedReason } from '@/components/system-screens'
import { ScanSessionProvider } from '@/features/scan/scan-session-context'
import { classifyError } from '@/features/system/error-recovery'
import { ActiveTenantProvider } from '@/features/tenant/tenant-context'
import { identifyMonitoringUser, trackPage } from '@/lib/monitoring'
import { AuthenticatedApolloProvider } from '@/providers/authenticated-apollo-provider'

const routeApi = getRouteApi('/t/$tenant_id')

function TenantContent() {
  const { tenant_id: tenantId } = routeApi.useParams()
  const { data, error, loading } = api.useFetchMeQuery()

  if (loading && !isSome(data)) {
    return <CenteredScreen aria-label="現場を読み込み中" />
  }
  if (isSome(error)) {
    return (
      <ErrorRecoveryScreen
        kind={classifyError(error)}
        message={error.message}
        onHome={() => {
          window.location.assign('/')
        }}
        onReload={() => {
          window.location.reload()
        }}
        onSignOut={() => {
          window.location.assign('/sign_out')
        }}
      />
    )
  }
  if (!isSome(data) || data.me.tenant.id !== tenantId) {
    return <Navigate to="/" replace />
  }

  return <ResolvedTenantContent me={data.me} tenantId={tenantId} />
}

function ResolvedTenantContent({ me, tenantId }: { me: MeFieldsFragment; tenantId: string }) {
  const location = useLocation()
  const { tenant } = me
  const monitoringUser = {
    email: me.email,
    id: me.id,
    name: me.name,
    role: me.role,
    tenantId: tenant.id,
    tenantName: tenant.name,
  }

  useEffect(() => {
    identifyMonitoringUser(monitoringUser)
    trackPage(location.pathname.replace(tenantId, '[tenant_id]'), monitoringUser)
  }, [location.pathname, me.email, me.id, me.name, me.role, tenant.id, tenant.name, tenantId])

  // スマホアプリはメタルタグスキャン専用のため、ステータス（未契約・停止）に加えて
  // プラン（お試しプラン・ライトプランはメタルタグ機能なし）でも入口で止める
  const accessDeniedReason = getAccessDeniedReason(tenant)
  if (isSome(accessDeniedReason)) {
    return (
      <>
        <BusinessTransferNotice me={me} />
        <TenantAccessDeniedScreen
          reason={accessDeniedReason}
          onLogout={() => {
            window.location.assign('/sign_out')
          }}
        />
      </>
    )
  }
  return (
    <ActiveTenantProvider
      value={{
        id: tenant.id,
        name: tenant.name,
        company: tenant.companyName,
        location: tenant.prefecture,
        buildingParts: tenant.setting?.millsheetBuildingParts ?? [],
      }}
    >
      <ScanSessionProvider tenantId={tenant.id}>
        <Outlet />
        <BusinessTransferNotice key={`${tenant.id}:${me.id}`} me={me} />
      </ScanSessionProvider>
    </ActiveTenantProvider>
  )
}

function getAccessDeniedReason(tenant: MeFieldsFragment['tenant']): TenantAccessDeniedReason | undefined {
  // プラン判定を先に見る。お試しプランは status（free）で表現されており isScanAvailable も false を返すが、
  // 理由の表示は「ステータスが無効」ではなく「お試しプランではスマホアプリを使えない」が正しい
  const planName = metalTagUnavailablePlanName(tenant)
  if (isSome(planName)) {
    return { kind: 'plan', planName }
  }
  if (!isScanAvailable(tenant.status)) {
    return { kind: 'status', status: tenant.status }
  }
  return undefined
}

function TenantRoutePage() {
  const { tenant_id: tenantId } = routeApi.useParams()
  return (
    <AuthenticatedApolloProvider tenantId={tenantId}>
      <TenantContent />
    </AuthenticatedApolloProvider>
  )
}

export const Route = createFileRoute('/t/$tenant_id')({ component: TenantRoutePage })
